<template>
  <div>
    <v-dialog v-model="isModalActiveLocal" persistent max-width="600px">
      <v-form
        ref="paymentMethodForm"
        >
        <v-card>
          <v-card-title>
            <span class="pt-5 headline">Add Payment Method</span>
          </v-card-title>
          <v-card-text>
            <v-form>
              <v-container>
                <v-alert v-if="error" type="error" dense class="mb-4">{{ error }}</v-alert>
                <v-row dense>
                  <v-col cols="12">
                    <v-text-field
                      v-if="!cardValid"
                      label="Cardholder Number"
                      id="cc-num"
                      ref="credit_card_num"
                      outlined
                      dense
                      v-model="payment.card_number"
                      @blur="getCardType"
                      :prepend-inner-icon="brandIcon"
                      autocomplete="cc-number"
                      inputmode="numeric"
                    ></v-text-field>
                    <v-text-field
                      v-else
                      label="Cardholder Number"
                      outlined
                      dense
                      readonly
                      v-model="formattedCard"
                      :prepend-inner-icon="brandIcon"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      label="Cardholder Name"
                      id="cc-name"
                      outlined
                      dense
                      v-model="payment.card_name"
                      autocomplete="cc-name"
                      :rules="[v => !!v || 'Required']"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      v-model="payment.card_expiry_month"
                      label="MM"
                      :rules="[v => /^(0?[1-9]|1[0-2])$/.test(v) || '1-12']"
                      autocomplete="cc-exp-month"
                      inputmode="numeric"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      v-model="payment.card_expiry_year"
                      label="YYYY"
                      :rules="[v => /^\d{4}$/.test(v) || 'YYYY']"
                      autocomplete="cc-exp-year"
                      inputmode="numeric"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      label="CVV"
                      id="cc-cvv"
                      outlined
                      dense
                      v-model="payment.card_cvv"
                      :rules="[v => /^\d{3,4}$/.test(v) || '3–4 digits']"
                      autocomplete="cc-csc"
                      inputmode="numeric"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-select
                      label="Billing Address"
                      :items="billingAddresses"
                      return-object
                      outlined
                      dense
                      v-model="selectedAddress"
                      item-text="text"
                      required
                    ></v-select>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="close">Cancel</v-btn>
            <v-btn
              color="blue darken-1"
              :loading="addingPaymentLoader"
              :disabled="disableForm"
              text
              @click="submit">
              Add Payment Method
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import Payment from 'payment'

export default {
  name: 'AddPaymentModal',
  props: {
    isModalActive: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      payment: {
        id: null,
        card_name: '',
        card_number: '',
        card_token: '',
        card_expiry_month: '',
        card_expiry_year: '',
        card_expiry_formatted: '',
        card_type: '',
        card_cvv: '',
        address_id: null,
        processor: 'AUTHORIZE',
        primary: false,
        active: true,
        accountable_id: null,
        accountable_type: null
      },
      authData: {
        clientKey: process.env.VUE_APP_AUTHORIZENET_CLIENT_KEY,
        apiLoginID: process.env.VUE_APP_AUTHORIZENET_LOGIN_ID
      },
      error: '',
      brandIcon: 'mdi-credit-card',
      billingAddresses: [],
      selectedAddress: null,
      addingPaymentLoader: false,
      acceptReady: false,
      cardValid: false,
      formattedCard: ''
    }
  },
  filters: {
    cardNumber (token) {
      return token.slice(-4)
    },
    capitalize (string) {
      if (!string) return ''
      string = string.toString()
      return string.charAt(0).toUpperCase() + string.slice(1)
    },
    primary (value) {
      if (value) return 'Primary'

      return ''
    }
  },
  computed: {
    ...mapState('auth', {
      user: state => state.user
    }),
    ...mapState('contractorManagement', {
      contractor: state => state.contractor
    }),
    isModalActiveLocal: {
      get () {
        return this.isModalActive
      },
      set (val) {
        if (!val) {
          this.$emit('close-modal')
        }
      }
    },
    valid () {
      return this.payment.card_number !== '' && this.payment.card_expiry_month !== '' && this.payment.card_cvv !== '' && this.payment.card_name !== '' && this.payment.card_type !== ''
    },
    disableForm () {
      return !this.valid || this.addingPaymentLoader
    }
  },
  methods: {
    ...mapActions('paymentManagement', {
      authorize: 'authorizePayment',
      add: 'addPayment',
      fetch:'fetchPayments'
    }),
    init () {
      if (window.Accept && window.Accept.dispatchData) {
        this.acceptReady = true
      } else {
        console.error('Failed to load payment library')
      }

      this.setBillingAddresses()
    },
    setBillingAddresses () {
      this.billingAddresses = this.contractor.addresses.map((address) => ({
        ...address,
        text: `${address.street1} ${address.city}, ${address.state.abbreviation} ${address.zip}`
      }))
    },
    close () {
      this.payment = {
        id: null,
        card_name: '',
        card_number: '',
        card_token: '',
        card_expiry_month: '',
        card_expiry_year: '',
        card_expiry_formatted: '',
        card_type: '',
        card_cvv: '',
        address_id: null,
        processor: 'AUTHORIZE',
        primary: false,
        active: true,
        accountable_id: null,
        accountable_type: null
      }
      this.addingPaymentLoader = false
      this.$refs.paymentMethodForm.resetValidation()
      this.$emit('close-modal')
    },
    addCard (token) {
      if (!token.opaqueData) {
        this.error = token.messages.message[0].text
        this.addingPaymentLoader = false
        return
      }

      const obj = {
        model_type: 'contractor',
        model_id: this.contractor.id,
        card_name: this.payment.card_name,
        card_number: this.formattedCard,
        card_expiry: `${this.payment.card_expiry_month  } / ${  this.payment.card_expiry_year}`,
        card_type: this.payment.card_type,
        address_id: this.selectedAddress.id,
        primary: true,
        opaqueData: token.opaqueData
      }

      this.add(obj)
        .then(() => {
          this.payment.card_number = ''
          this.payment.card_expiry_month = ''
          this.payment.card_expiry_year = ''
          this.payment.brand_type = ''
          this.payment.zip = ''
          this.payment.card_name = ''
          this.selectedAddress = []
          this.brandIcon = 'mdi-credit-card'

          this.fetch()

          this.$emit('add-payment')
          this.close()
          this.$toast.success('Payment stored!')
        })
        .catch((error) => {
          this.$toast.error(error.data ? error.data.error : error)
        })
        .finally(() => {
          this.addingPaymentLoader = false
        })
    },
    async submit () {
      this.error = ''
      this.token = null

      this.addingPaymentLoader = true
      const secureData = {
        cardData: {
          cardNumber: this.payment.card_number.replace(/ /g, ''),
          month: this.payment.card_expiry_month,
          year: this.payment.card_expiry_year,
          cardCode: this.payment.card_cvv,
          zip: this.selectedAddress.zip
        },
        authData: this.authData
      }

      this.$accept.dispatch(secureData, this.addCard)
    },
    getCardType () {
      this.cardValid = false

      if (Payment.fns.validateCardNumber(this.payment.card_number)) {
        this.cardValid = true
        this.payment.card_token = this.payment.card_number
        this.payment.card_type = Payment.fns.cardType(this.payment.card_number)
        this.brandIcon = this.$utils.getCardTypeIcon(this.payment.card_type)
        const cardPrefix = this.payment.card_type.toLowerCase() === 'amex' ? 'XXX-XXXX-XXXX-' : 'XXXX-XXXX-XXXX-'
        const last4 = this.payment.card_number.slice(-4)
        this.formattedCard = cardPrefix + last4
      }
    }
  },
  mounted () {
    this.init()

    this.$nextTick(() => {
      const numberInput = this.$refs.credit_card_num.$el.querySelector('input')

      if (numberInput) Payment.formatCardNumber(numberInput)
    })
  }
}
</script>

<style scoped>

</style>
